import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { EMPTY, Subject, combineLatest } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';

import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { PendingOrderPanelComponent } from '../../shared/components/pending-order-panel/pending-order-panel.component';
import { PendingOrderPanelItem } from '../../core/models/pending-order.model';
import {
  filterPendingOrdersForDisplay,
  getPendingOrdersSortParams,
  mapPendingOrderToPanelItem,
  resolvePendingOrdersDocument,
} from '../../core/utils/pending-order.util';
import { shouldAccessPendingOrders } from '../../core/utils/pending-orders-eligibility.util';
import { AuthService } from '../../core/services/auth.service';
import { UserFacade } from '../../core/store/user/user.facade';
import { PendingOrdersService } from '../../core/services/pending-orders.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { LoadingComponent } from '../../shared/components/loading/loading.component';

@Component({
  selector: 'umkt-pending-orders',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    PendingOrderPanelComponent,
    I18nPipe,
    LoadingComponent,
  ],
  templateUrl: './pending-orders.component.html',
  styleUrls: ['./pending-orders.component.scss'],
})
export class PendingOrdersComponent implements OnInit, OnDestroy {
  breadcrumbItems: BreadcrumbItem[] = [];

  currentPage = 0;
  itemsPerPage = 5;
  private baseItemsPerPageOptions: number[] = [5, 10, 15, 20];

  selectedOrder = 'date-newest';
  orderOptions = [
    { value: 'date-newest', label: 'pages.orders.sort.newest' },
    { value: 'date-oldest', label: 'pages.orders.sort.oldest' },
  ];

  currentOrders: PendingOrderPanelItem[] = [];
  totalElements = 0;
  totalPagesFromServer = 0;
  isLoading = false;
  error: string | null = null;
  isSelectorFocused = false;

  private destroy$ = new Subject<void>();
  private _itemsPerPageOptions: number[] = [];
  private _lastTotalElements = -1;
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private userFacade = inject(UserFacade);
  private pendingOrdersService = inject(PendingOrdersService);
  private i18nService = inject(I18nService);


  ngOnInit(): void {
    combineLatest([this.userFacade.getLoginData().pipe(take(1))])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([loginData]) => {
        if (!shouldAccessPendingOrders(loginData, this.authService)) {
          this.router.navigate(['/my-account/orders']);
          return;
        }

        this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
          this.syncStateFromQueryParams(params);
          this.updateBreadcrumb();
          this.loadOrders();
        });
      });

    this.i18nService
      .getCurrentLanguage()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updateBreadcrumb());
  }

  private syncStateFromQueryParams(params: Params): void {
    if (params['page'] !== undefined) {
      this.currentPage = +params['page'] - 1;
    }
    if (params['size'] !== undefined) {
      this.itemsPerPage = +params['size'];
    }
    if (params['sort'] !== undefined) {
      this.selectedOrder = params['sort'];
    }
  }

  private loadOrders(): void {
    this.isLoading = true;
    this.error = null;

    combineLatest([
      this.userFacade.getUserDetails().pipe(take(1)),
      this.userFacade.getLoginData().pipe(take(1)),
    ])
      .pipe(
        take(1),
        switchMap(([user, loginData]) => {
          const document = resolvePendingOrdersDocument(user, loginData);
          if (!document) {
            this.isLoading = false;
            this.error = this.i18nService.instant('pages.pendingOrders.errors.cnpj');
            return EMPTY;
          }

          const sort = getPendingOrdersSortParams(this.selectedOrder);
          return this.pendingOrdersService.getRequestsOrders(document, {
            page: this.currentPage,
            size: this.itemsPerPage,
            sortField: sort.sortField,
            sortDirection: sort.sortDirection,
          });
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: response => {
          this.isLoading = false;
          const content = response.content ?? [];
          const visible = filterPendingOrdersForDisplay(content);
          const removedOnPage = content.length - visible.length;

          this.currentOrders = visible.map(mapPendingOrderToPanelItem);
          this.totalElements = Math.max(
            0,
            (response.page?.totalElements ?? 0) - removedOnPage
          );
          this.totalPagesFromServer =
            Math.ceil(this.totalElements / this.itemsPerPage) || 0;

          if (response.page?.number !== undefined) {
            this.currentPage = response.page.number;
          }

          this.clearItemsPerPageCache();
        },
        error: () => {
          this.isLoading = false;
          this.error = this.i18nService.instant('pages.pendingOrders.errors.load');
          this.currentOrders = [];
          this.totalElements = 0;
          this.totalPagesFromServer = 0;
        },
      });
  }

  private updateBreadcrumb(): void {
    this.breadcrumbItems = [
      {
        label: this.i18nService.instant('shared.labels.myAccount'),
        url: '/my-account',
      },
      {
        label: this.i18nService.instant('pages.pendingOrders.title'),
        url: '/my-account/pending-orders',
      },
    ];
  }

  onCompleteData(token: string): void {
    if (!token) {
      return;
    }
    this.router.navigate(['/my-account/pending-orders/complete'], {
      queryParams: { token },
    });
  }

  onOrderChange(): void {
    this.currentPage = 0;
    this.updateUrlParams();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.updateUrlParams();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  onItemsPerPageChange(newItemsPerPage: number): void {
    this.itemsPerPage = newItemsPerPage;
    this.currentPage = 0;
    this.updateUrlParams();
  }

  private updateUrlParams(): void {
    const queryParams: Record<string, number | string> = {};

    if (this.currentPage > 0) {
      queryParams['page'] = this.currentPage + 1;
    }
    if (this.itemsPerPage !== 5) {
      queryParams['size'] = this.itemsPerPage;
    }
    if (this.selectedOrder !== 'date-newest') {
      queryParams['sort'] = this.selectedOrder;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
      replaceUrl: true,
    });
  }

  get totalPages(): number {
    return this.totalPagesFromServer;
  }

  get itemsPerPageOptions(): number[] {
    if (
      this._lastTotalElements === this.totalElements &&
      this._itemsPerPageOptions.length > 0
    ) {
      return this._itemsPerPageOptions;
    }

    this._lastTotalElements = this.totalElements;
    const totalItems = this.totalElements;

    if (totalItems === 0) {
      this._itemsPerPageOptions = [...this.baseItemsPerPageOptions];
      return this._itemsPerPageOptions;
    }

    const validOptions = this.baseItemsPerPageOptions.filter(
      option => option <= totalItems
    );
    const nextLargerOption = this.baseItemsPerPageOptions.find(
      option => option > totalItems
    );

    if (nextLargerOption && !validOptions.includes(nextLargerOption)) {
      validOptions.push(nextLargerOption);
    }

    this._itemsPerPageOptions =
      validOptions.length > 0
        ? validOptions.sort((a, b) => a - b)
        : [...this.baseItemsPerPageOptions];

    if (!this._itemsPerPageOptions.includes(this.itemsPerPage)) {
      this.itemsPerPage = Math.max(...this._itemsPerPageOptions);
    }

    return this._itemsPerPageOptions;
  }

  private clearItemsPerPageCache(): void {
    this._itemsPerPageOptions = [];
    this._lastTotalElements = -1;
  }

  onItemsPerPageSelectorChange(newItemsPerPage: number): void {
    this.onItemsPerPageChange(Number(newItemsPerPage));
  }

  onSelectorFocus(): void {
    this.isSelectorFocused = true;
  }

  onSelectorBlur(): void {
    this.isSelectorFocused = false;
  }

  getPageInfoText(): string {
    if (this.totalElements === 0) {
      return 'pages.orders.paginationInfoEmpty';
    }
    return 'pages.orders.paginationInfo';
  }

  getPageInfoParams(): Record<string, number> {
    if (this.totalElements === 0) {
      return {};
    }

    const startItem = this.currentPage * this.itemsPerPage + 1;
    const endItem = Math.min(
      (this.currentPage + 1) * this.itemsPerPage,
      this.totalElements
    );

    return {
      start: startItem,
      end: endItem,
      total: this.totalElements,
    };
  }

  getVisiblePages(): number[] {
    const totalPages = this.totalPages;
    const currentPage = this.currentPage;
    const visiblePages: number[] = [];

    if (totalPages <= 7) {
      for (let i = 0; i < totalPages; i++) {
        visiblePages.push(i);
      }
      return visiblePages;
    }

    if (currentPage <= 3) {
      for (let i = 0; i < 5; i++) {
        visiblePages.push(i);
      }
      visiblePages.push(-1, totalPages - 1);
    } else if (currentPage >= totalPages - 4) {
      visiblePages.push(0, -1);
      for (let i = totalPages - 5; i < totalPages; i++) {
        visiblePages.push(i);
      }
    } else {
      visiblePages.push(0, -1, currentPage - 1, currentPage, currentPage + 1, -1, totalPages - 1);
    }

    return visiblePages;
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages && page !== this.currentPage) {
      this.onPageChange(page);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.goToPage(this.currentPage - 1);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.goToPage(this.currentPage + 1);
    }
  }

  formatItemsPerPageOption(option: number): string {
    return option.toString().padStart(2, '0');
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
